import React, { useState, useEffect, useRef } from 'react';
import {
  AppBar, Toolbar, Typography, Box, Tabs, Tab, Card, CardContent,
  Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Fab, List, ListItem, ListItemText, ListItemAvatar, Avatar, Chip,
  Divider, MenuItem, Select, FormControl, InputLabel, InputAdornment,
  IconButton, Snackbar, Alert, Paper, FormControlLabel, Checkbox, Tooltip, Slider,
} from '@mui/material';
import {
  Add as AddIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Repeat as RepeatIcon,
  Logout as LogoutIcon,
  Language as LanguageIcon,
  OpenInNew as OpenInNewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ViewList as ViewListIcon,
  CalendarViewMonth as CalendarViewMonthIcon,
  Gavel as GavelIcon,
  QueryStats as QueryStatsIcon,
  Assignment as LineupIcon,
  Lock as LockIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
} from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';
import { EVENT_TYPES, WEEKDAYS, RECURRING_INTERVALS, POSITIONS, UWR_LINKS, FILTERS, PLAYER_POS_FILTERS, EXCLUSIVE_FILTER_GROUPS, emptyEvent, emptyPlayer } from './constants';
import { computeAutoTeam } from './utils/players';
import { expandRecurringEvents } from './utils/events';
import { groupEventsByDate, parseLocalDate, WEEKDAY_DE, MONTH_DE } from './utils/date';
import { hasLineupAssignments } from './utils/lineup';
import TabPanel from './components/TabPanel';
import RoleSelectScreen from './components/RoleSelectScreen';
import SetPasswordDialog from './components/SetPasswordDialog';
import ChangePasswordDialog from './components/ChangePasswordDialog';
import PlayerDetailDialog from './components/PlayerDetailDialog';
import EventDetailDialog from './components/EventDetailDialog';
import CalendarView from './components/CalendarView';
import LineupDialog from './components/LineupDialog';

const getEventMeta = (type) => EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[0];

const SKILL_FIELDS = [
  { key: 'defense', label: 'Abwehr' },
  { key: 'scoring', label: 'Torabschluss' },
  { key: 'deckel', label: 'Deckel' },
  { key: 'verteidigung', label: 'Verteidigung' },
  { key: 'sturm', label: 'Sturm' },
  { key: 'oneOnOne', label: '1 gegen 1' },
  { key: 'positioning', label: 'Positionsspiel' },
  { key: 'availability', label: 'Verfügbarkeit' },
  { key: 'fitness', label: 'Fitness' },
  { key: 'teamplay', label: 'Teamspiel' },
];

const skillValue = (stats = {}) => {
  const values = SKILL_FIELDS
    .map(({ key }) => Number(stats?.[key]))
    .filter(v => Number.isFinite(v));
  if (!values.length) return null;
  return Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10;
};

const topSkills = (stats = {}, count = 3) => SKILL_FIELDS
  .map(({ key, label }) => ({ key, label, value: Number(stats?.[key]) }))
  .filter(s => Number.isFinite(s.value))
  .sort((a, b) => b.value - a.value)
  .slice(0, count);

const comparePlayerNamesAsc = (a, b) =>
  (a.lastName || '').localeCompare(b.lastName || '', 'de') ||
  (a.firstName || '').localeCompare(b.firstName || '', 'de');

const getSkillStatValue = (player, skillKey) => {
  const value = Number(player?.skillStats?.[skillKey]);
  return Number.isFinite(value) ? value : null;
};

const parseSkillSort = (playerSort) => {
  const match = /^skill-(.+)-(asc|desc)$/.exec(playerSort);
  if (!match) return null;
  return { key: match[1], dir: match[2] };
};

export default function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [tab, setTab] = useState(0);
  const [events, setEvents] = useState([]);
  const [players, setPlayers] = useState([]);
  const [eventDialog, setEventDialog] = useState(false);
  const [playerDialog, setPlayerDialog] = useState(false);
  const [detailEvent, setDetailEvent] = useState(null);
  const [detailPlayer, setDetailPlayer] = useState(null);
  const [detailPlayerMode, setDetailPlayerMode] = useState('default');
  const [editingEventId, setEditingEventId] = useState(null);
  const [editingPlayerId, setEditingPlayerId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: null, item: null });
  const [formData, setFormData] = useState(emptyEvent);
  const [playerData, setPlayerData] = useState(emptyPlayer);
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const [activePlayerFilters, setActivePlayerFilters] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState(['past']);
  const [expandPastAll, setExpandPastAll] = useState(false);
  const [playerSort, setPlayerSort] = useState('name-asc');
  const [eventView, setEventView] = useState('list');
  const [calMonth, setCalMonth] = useState(() => { const n = new Date(); return { year: n.getFullYear(), month: n.getMonth() }; });
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [statsFilter, setStatsFilter] = useState('all');
  const [statsStartDate, setStatsStartDate] = useState('');
  const [statsEndDate, setStatsEndDate] = useState('');
  const [statsSort, setStatsSort] = useState({ col: 'name', dir: 'asc' });
  const [statsSearch, setStatsSearch] = useState('');
  const [lineupDialogEvent, setLineupDialogEvent] = useState(null);
  const [reopenEventAfterEdit, setReopenEventAfterEdit] = useState(null);
  const [reopenEventAfterPlayerDetail, setReopenEventAfterPlayerDetail] = useState(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminSort, setAdminSort] = useState({ col: 'name', dir: 'asc' });
  const [setPwdDialog, setSetPwdDialog] = useState({ open: false, playerId: null, playerName: '', firstName: '', loginEmail: '', requireEmail: false });
  const [skillsOnlyEdit, setSkillsOnlyEdit] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const touchSkillEditArmedRef = useRef({});

  const handleSkillSliderPointerDown = (key, event) => {
    const pointerType = event?.nativeEvent?.pointerType;
    if (pointerType !== 'touch') return;
    const target = event.target;
    const thumbTouched = typeof target.closest === 'function' && !!target.closest('.MuiSlider-thumb');
    touchSkillEditArmedRef.current[key] = thumbTouched;
  };

  const disarmSkillSliderTouch = (key) => {
    touchSkillEditArmedRef.current[key] = false;
  };

  const handleSkillSliderChange = (key) => (event, value) => {
    const isTouch = event?.nativeEvent?.pointerType === 'touch' || event?.type?.startsWith('touch');
    if (isTouch && !touchSkillEditArmedRef.current[key]) return;
    const nextValue = Array.isArray(value) ? value[0] : value;
    setPlayerData(prev => ({
      ...prev,
      skillStats: {
        ...prev.skillStats,
        [key]: nextValue,
      },
    }));
  };

  const handleSelectUser = (user) => {
    setCurrentUser(user);
    if (user.mustChangePassword) setChangePasswordOpen(true);
    refreshEvents();
    refreshPlayers();
  };

  const handleSwitchRole = async () => {
    try { await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }); } catch {}
    setCurrentUser(null);
  };

  const canCreate = currentUser?.role === 'admin' || currentUser?.role === 'trainer' || currentUser?.role === 'player';
  const canManagePlayers = currentUser?.role === 'admin';

  const refreshEvents = () => {
    fetch('/api/events', { credentials: 'include' }).then(r => r.json()).then(d => setEvents(Array.isArray(d) ? d : [])).catch(() => {});
  };

  const refreshPlayers = () => {
    fetch('/api/players', { credentials: 'include' }).then(r => r.json()).then(d => setPlayers(Array.isArray(d) ? d : [])).catch(() => {});
  };

  const fetchAdminUsers = () => {
    setAdminLoading(true);
    fetch('/api/admin/users', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setAdminUsers(Array.isArray(d) ? d : []); setAdminLoading(false); })
      .catch(() => setAdminLoading(false));
  };

  const openEventDetail = async (eventToOpen) => {
    if (!eventToOpen) return;
    try {
      const res = await fetch(`/api/events/${eventToOpen.id}?t=${Date.now()}`, { cache: 'no-store', credentials: 'include' });
      if (!res.ok) throw new Error('event fetch failed');
      const fresh = await res.json();
      setEvents(prev => prev.map(e => e.id === fresh.id ? fresh : e));
      if (eventToOpen.recurring && eventToOpen.date) {
        const occurrenceDate = eventToOpen.occurrenceDate || eventToOpen.date;
        setDetailEvent({
          ...fresh,
          date: occurrenceDate,
          occurrenceDate,
          _key: eventToOpen._key || `${eventToOpen.id}-${occurrenceDate}`,
          signups: fresh.occurrenceSignups?.[occurrenceDate] || [],
          declines: fresh.occurrenceDeclines?.[occurrenceDate] || [],
          gamesPlayed: fresh.occurrenceGamesPlayed?.[occurrenceDate] || {},
          gamesRefereed: fresh.occurrenceGamesRefereed?.[occurrenceDate] || {},
          lineup: fresh.occurrenceLineups?.[occurrenceDate] ?? null,
          lineupPublished: fresh.occurrenceLineupPublished?.[occurrenceDate] ?? false,
        });
        return;
      }
      setDetailEvent(fresh);
    } catch {
      setDetailEvent(eventToOpen);
    }
  };

  const closeEventDialogAndMaybeReopen = () => {
    setEventDialog(false);
    setEditingEventId(null);
    setFormData(emptyEvent);
    if (reopenEventAfterEdit) {
      const eventToReopen = reopenEventAfterEdit;
      setReopenEventAfterEdit(null);
      openEventDetail(eventToReopen);
    }
  };

  const closePlayerDetailAndMaybeReopenEvent = () => {
    setDetailPlayer(null);
    setDetailPlayerMode('default');
    if (reopenEventAfterPlayerDetail) {
      const eventToReopen = reopenEventAfterPlayerDetail;
      setReopenEventAfterPlayerDetail(null);
      openEventDetail(eventToReopen);
    }
  };

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });
  const resetPlayer = () => setPlayerData({ ...emptyPlayer, skillStats: { ...emptyPlayer.skillStats } });
  const capLabel = (cap) => (cap == null || String(cap).trim() === '' ? 'N/A' : `#${cap}`);
  const openPlayerSkillEditor = (player) => {
    setDetailPlayer(null);
    setDetailPlayerMode('default');
    setPlayerData({
      ...player,
      skillStats: { ...emptyPlayer.skillStats, ...(player?.skillStats || {}) },
    });
    setEditingPlayerId(player.id);
    setSkillsOnlyEdit(true);
    setPlayerDialog(true);
  };

  useEffect(() => {
    fetch('/api/auth/me', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(user => { if (user) setCurrentUser(user); setInitializing(false); })
      .catch(() => setInitializing(false));
    refreshEvents();
    refreshPlayers();
    const interval = setInterval(() => { refreshEvents(); refreshPlayers(); }, 10000);
    const onVisible = () => { if (document.visibilityState === 'visible') { refreshEvents(); refreshPlayers(); } };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisible); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (events.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const evId = params.get('event');
    if (evId) {
      const ev = events.find(e => String(e.id) === evId);
      if (ev) { openEventDetail(ev); window.history.replaceState({}, '', window.location.pathname); }
    }
  }, [events]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === 4 && currentUser?.role === 'admin') fetchAdminUsers();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentUser?.role !== 'admin' && tab >= 4) setTab(3);
  }, [currentUser?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  if (initializing) return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">Lade…</Typography>
      </Box>
    </ThemeProvider>
  );
  if (!currentUser) return <RoleSelectScreen onSelect={handleSelectUser} />;

  const handleSetPassword = async (playerId, password, email) => {
    const r = await fetch(`/api/admin/players/${playerId}/set-password`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password, email }),
    });
    if (r.ok) {
      fetchAdminUsers();
      if (email) setPlayers(prev => prev.map(p => p.id === playerId ? { ...p, email } : p));
      return null;
    } else {
      const d = await r.json().catch(() => ({}));
      return d.message || 'Fehler beim Setzen des Passworts.';
    }
  };

  const handleRoleChange = async (email, newRole) => {
    const r = await fetch(`/api/admin/users/${encodeURIComponent(email)}/role`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role: newRole }),
    });
    if (r.ok) { fetchAdminUsers(); } else {
      const d = await r.json().catch(() => ({}));
      showSnack(d.message || 'Fehler beim Ändern der Rolle.', 'error');
    }
  };

  const handleSignup = async (id, occurrenceDate, targetEmail = null) => {
    const email = targetEmail || (currentUser.playerId ? `player-${currentUser.playerId}` : currentUser.email);
    const event = events.find(e => e.id === id);
    if (!event) return;
    const signupList = occurrenceDate && event.recurring ? (event.occurrenceSignups?.[occurrenceDate] || []) : (event.signups || []);
    const alreadySignedUp = signupList.includes(email);
    if (alreadySignedUp) {
      const res = await fetch(`/api/events/${id}/signup`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ occurrenceDate, ...(targetEmail ? { targetEmail } : {}) }),
      });
      if (res.ok) {
        const updated = await res.json();
        setEvents(prev => prev.map(e => e.id === id ? updated : e));
        if (occurrenceDate && updated.recurring) {
          setDetailEvent(prev => prev ? {
            ...prev,
            occurrenceDate,
            occurrenceSignups: {
              ...(prev.occurrenceSignups || {}),
              [occurrenceDate]: updated.occurrenceSignups?.[occurrenceDate] || [],
            },
            occurrenceDeclines: {
              ...(prev.occurrenceDeclines || {}),
              [occurrenceDate]: updated.occurrenceDeclines?.[occurrenceDate] || [],
            },
            signups: updated.occurrenceSignups?.[occurrenceDate] || [],
            declines: updated.occurrenceDeclines?.[occurrenceDate] || [],
          } : prev);
        } else { setDetailEvent(updated); }
        showSnack(targetEmail ? 'Anmeldung entfernt.' : 'Deine Zusage wurde entfernt.', 'info');
      }
    } else {
      const res = await fetch(`/api/events/${id}/signup`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ occurrenceDate, ...(targetEmail ? { targetEmail } : {}) }),
      });
      if (res.ok) {
        const updated = await res.json();
        setEvents(prev => prev.map(e => e.id === id ? updated : e));
        if (occurrenceDate && updated.recurring) {
          setDetailEvent(prev => prev ? {
            ...prev,
            occurrenceDate,
            occurrenceSignups: {
              ...(prev.occurrenceSignups || {}),
              [occurrenceDate]: updated.occurrenceSignups?.[occurrenceDate] || [],
            },
            occurrenceDeclines: {
              ...(prev.occurrenceDeclines || {}),
              [occurrenceDate]: updated.occurrenceDeclines?.[occurrenceDate] || [],
            },
            signups: updated.occurrenceSignups?.[occurrenceDate] || [],
            declines: updated.occurrenceDeclines?.[occurrenceDate] || [],
          } : prev);
        } else { setDetailEvent(updated); }
        showSnack(targetEmail ? 'Spieler angemeldet!' : 'Du hast zugesagt!');
      } else {
        const d = await res.json().catch(() => ({}));
        showSnack(d.message || 'Fehler beim Anmelden.', 'error');
      }
    }
  };

  const handleDecline = async (id, occurrenceDate, targetEmail = null) => {
    const email = targetEmail || (currentUser.playerId ? `player-${currentUser.playerId}` : currentUser.email);
    const event = events.find(e => e.id === id);
    if (!event) return;
    const declineList = occurrenceDate && event.recurring ? (event.occurrenceDeclines?.[occurrenceDate] || []) : (event.declines || []);
    const alreadyDeclined = declineList.includes(email);
    if (alreadyDeclined) {
      const res = await fetch(`/api/events/${id}/decline`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ occurrenceDate, ...(targetEmail ? { targetEmail } : {}) }),
      });
      if (res.ok) {
        const updated = await res.json();
        setEvents(prev => prev.map(e => e.id === id ? updated : e));
        if (occurrenceDate && updated.recurring) {
          setDetailEvent(prev => prev ? {
            ...prev,
            occurrenceDate,
            occurrenceSignups: {
              ...(prev.occurrenceSignups || {}),
              [occurrenceDate]: updated.occurrenceSignups?.[occurrenceDate] || [],
            },
            occurrenceDeclines: {
              ...(prev.occurrenceDeclines || {}),
              [occurrenceDate]: updated.occurrenceDeclines?.[occurrenceDate] || [],
            },
            signups: updated.occurrenceSignups?.[occurrenceDate] || [],
            declines: updated.occurrenceDeclines?.[occurrenceDate] || [],
          } : prev);
        } else { setDetailEvent(updated); }
        showSnack(targetEmail ? 'Absage entfernt.' : 'Deine Absage wurde entfernt.', 'info');
      }
    } else {
      const res = await fetch(`/api/events/${id}/decline`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ occurrenceDate, ...(targetEmail ? { targetEmail } : {}) }),
      });
      if (res.ok) {
        const updated = await res.json();
        setEvents(prev => prev.map(e => e.id === id ? updated : e));
        if (occurrenceDate && updated.recurring) {
          setDetailEvent(prev => prev ? {
            ...prev,
            occurrenceDate,
            occurrenceSignups: {
              ...(prev.occurrenceSignups || {}),
              [occurrenceDate]: updated.occurrenceSignups?.[occurrenceDate] || [],
            },
            occurrenceDeclines: {
              ...(prev.occurrenceDeclines || {}),
              [occurrenceDate]: updated.occurrenceDeclines?.[occurrenceDate] || [],
            },
            signups: updated.occurrenceSignups?.[occurrenceDate] || [],
            declines: updated.occurrenceDeclines?.[occurrenceDate] || [],
          } : prev);
        } else { setDetailEvent(updated); }
        showSnack(targetEmail ? 'Spieler abgemeldet.' : 'Du hast abgesagt.', 'info');
      } else {
        const d = await res.json().catch(() => ({}));
        showSnack(d.message || 'Fehler beim Absagen.', 'error');
      }
    }
  };

  const handleDeleteEvent = async (id, occurrenceDate = null) => {
    try {
      if (occurrenceDate) {
        const res = await fetch(`/api/events/${id}/exclude-occurrence`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ date: occurrenceDate }),
        });
        if (res.ok) { refreshEvents(); setDeleteConfirm({ open: false, type: null, item: null }); showSnack('Termin entfernt.', 'info'); }
      } else {
        const res = await fetch(`/api/events/${id}`, { method: 'DELETE', credentials: 'include' });
        if (res.ok) {
          setEvents(prev => prev.filter(e => e.id !== id));
          setDetailEvent(null);
          setDeleteConfirm({ open: false, type: null, item: null });
          showSnack('Termin gelöscht.', 'info');
        }
      }
    } catch { showSnack('Fehler beim Löschen.', 'error'); }
  };

  const handleDeletePlayer = async (id) => {
    try {
      const res = await fetch(`/api/players/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        setPlayers(prev => prev.filter(p => p.id !== id));
        if (currentUser?.role === 'admin') fetchAdminUsers();
        setDetailPlayer(null);
        setDeleteConfirm({ open: false, type: null, item: null });
        showSnack('Spieler gelöscht.', 'info');
      }
    } catch { showSnack('Fehler beim Löschen.', 'error'); }
  };

  const handleResetPlayerSkillStats = async (id) => {
    try {
      const res = await fetch(`/api/admin/players/${id}/reset-skill-stats`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const updated = await res.json();
        setPlayers(prev => prev.map(p => p.id === id ? updated : p));
        if (detailPlayer?.id === id) setDetailPlayer(updated);
        showSnack('Spielerwerte zurückgesetzt.', 'info');
      } else {
        const d = await res.json().catch(() => ({}));
        showSnack(d.message || 'Fehler beim Zurücksetzen.', 'error');
      }
    } catch {
      showSnack('Fehler beim Zurücksetzen.', 'error');
    }
  };

  const handleResetAllPlayerSkillStats = async () => {
    try {
      const res = await fetch('/api/admin/players/reset-skill-stats', {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        setPlayers(prev => prev.map(p => ({
          ...p,
          skillStats: {
            defense: 0,
            scoring: 0,
            deckel: 0,
            verteidigung: 0,
            sturm: 0,
            oneOnOne: 0,
            positioning: 0,
            availability: 0,
            fitness: 0,
            teamplay: 0,
          },
        })));
        if (detailPlayer) {
          setDetailPlayer(prev => prev ? {
            ...prev,
            skillStats: {
              defense: 0,
              scoring: 0,
              deckel: 0,
              verteidigung: 0,
              sturm: 0,
              oneOnOne: 0,
              positioning: 0,
              availability: 0,
              fitness: 0,
              teamplay: 0,
            },
          } : prev);
        }
        showSnack('Skillwerte aller Spieler zurückgesetzt.', 'info');
      } else {
        const d = await res.json().catch(() => ({}));
        showSnack(d.message || 'Fehler beim Zurücksetzen.', 'error');
      }
    } catch {
      showSnack('Fehler beim Zurücksetzen.', 'error');
    }
  };

  const handleResetPlayerStatistics = async (id) => {
    try {
      const res = await fetch(`/api/admin/players/${id}/reset-statistics`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        refreshEvents();
        showSnack('Spieler-Statistik zurückgesetzt.', 'info');
      } else {
        const d = await res.json().catch(() => ({}));
        showSnack(d.message || 'Fehler beim Zurücksetzen der Statistik.', 'error');
      }
    } catch {
      showSnack('Fehler beim Zurücksetzen der Statistik.', 'error');
    }
  };

  const handleResetAllPlayerStatistics = async () => {
    try {
      const res = await fetch('/api/admin/players/reset-statistics', {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        refreshEvents();
        showSnack('Statistik aller Spieler zurückgesetzt.', 'info');
      } else {
        const d = await res.json().catch(() => ({}));
        showSnack(d.message || 'Fehler beim Zurücksetzen der Statistik.', 'error');
      }
    } catch {
      showSnack('Fehler beim Zurücksetzen der Statistik.', 'error');
    }
  };

  const handleSaveLineup = async (eventId, lineupData, occurrenceDate) => {
    if (!currentUser?.email) { showSnack('Kein Benutzer angemeldet.', 'error'); return false; }
    try {
      const res = await fetch(`/api/events/${eventId}/lineup`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          ...(lineupData?.variantId ? lineupData : { lineup: lineupData }),
          ...(occurrenceDate ? { occurrenceDate } : {}),
        }),
      });
      if (res.ok) {
        const updated = await res.json();
        setEvents(prev => prev.map(e => e.id === eventId ? updated : e));
        if (occurrenceDate && updated.recurring) {
          setLineupDialogEvent(prev => ({
            ...updated, date: occurrenceDate,
            signups: updated.occurrenceSignups?.[occurrenceDate] || [],
            declines: updated.occurrenceDeclines?.[occurrenceDate] || [],
            lineup: updated.occurrenceLineups?.[occurrenceDate] ?? null,
            lineupPublished: updated.occurrenceLineupPublished?.[occurrenceDate] ?? false,
          }));
        } else { setLineupDialogEvent(updated); }
        showSnack('Aufstellung gespeichert!');
        return true;
      } else {
        let msg = `Fehler ${res.status}`;
        try { const d = await res.json(); msg = d.message || msg; }
        catch { try { const t = await res.text(); if (t) msg += `: ${t.substring(0, 80)}`; } catch {} }
        showSnack(msg, 'error');
        return false;
      }
    } catch (err) {
      showSnack(`Netzwerkfehler: ${err.message}`, 'error');
      return false;
    }
  };

  const handlePublishLineup = async (eventId, occurrenceDate, variantId) => {
    if (!currentUser?.email) { showSnack('Kein Benutzer angemeldet.', 'error'); return; }
    try {
      const res = await fetch(`/api/events/${eventId}/lineup/publish`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          ...(occurrenceDate ? { occurrenceDate } : {}),
          ...(variantId ? { variantId } : {}),
        }),
      });
      if (res.ok) {
        const updated = await res.json();
        setEvents(prev => prev.map(e => e.id === eventId ? updated : e));
        if (occurrenceDate && updated.recurring) {
          const published = updated.occurrenceLineupPublished?.[occurrenceDate] ?? false;
          setLineupDialogEvent(prev => ({
            ...updated, date: occurrenceDate,
            signups: updated.occurrenceSignups?.[occurrenceDate] || [],
            declines: updated.occurrenceDeclines?.[occurrenceDate] || [],
            lineup: updated.occurrenceLineups?.[occurrenceDate] ?? null,
            lineupPublished: published,
          }));
          showSnack(published ? 'Aufstellung veröffentlicht!' : 'Veröffentlichung zurückgezogen.', 'info');
        } else {
          setLineupDialogEvent(updated);
          showSnack(updated.lineupPublished ? 'Aufstellung veröffentlicht!' : 'Veröffentlichung zurückgezogen.', 'info');
        }
      } else {
        let msg = `Fehler ${res.status}`;
        try { const d = await res.json(); msg = d.message || msg; }
        catch { try { const t = await res.text(); if (t) msg += `: ${t.substring(0, 80)}`; } catch {} }
        showSnack(msg, 'error');
      }
    } catch (err) { showSnack(`Netzwerkfehler: ${err.message}`, 'error'); }
  };

  const handleEventSubmit = async () => {
    try {
      if (editingEventId) {
        const res = await fetch(`/api/events/${editingEventId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify(formData),
        });
        if (res.ok) {
          const ev = await res.json();
          setEvents(prev => prev.map(e => e.id === editingEventId ? ev : e));
          closeEventDialogAndMaybeReopen();
          showSnack('Termin gespeichert!');
        } else { const e = await res.json().catch(() => ({})); showSnack(e.message || 'Fehler beim Speichern.', 'error'); }
      } else {
        const res = await fetch('/api/events', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify(formData),
        });
        if (res.ok) {
          const ev = await res.json();
          setEvents(prev => [ev, ...prev]);
          closeEventDialogAndMaybeReopen();
          showSnack('Termin erfolgreich erstellt!');
        } else { const e = await res.json().catch(() => ({})); showSnack(e.message || 'Fehler beim Speichern.', 'error'); }
      }
    } catch { showSnack('Fehler beim Speichern.', 'error'); }
  };

  const handlePlayerSubmit = async () => {
    try {
      if (editingPlayerId) {
        const body = skillsOnlyEdit ? { skillStats: playerData.skillStats, primaryPosition: playerData.primaryPosition, secondaryPosition: playerData.secondaryPosition } : playerData;
        const res = await fetch(`/api/players/${editingPlayerId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify(body),
        });
        if (res.ok) {
          const pl = await res.json();
          setPlayers(prev => prev.map(p => p.id === editingPlayerId ? pl : p));
          if (detailPlayer?.id === editingPlayerId) setDetailPlayer(pl);
          setPlayerDialog(false); setEditingPlayerId(null); resetPlayer(); setSkillsOnlyEdit(false);
          showSnack('Spieler gespeichert!');
          if (currentUser?.role === 'admin') fetchAdminUsers();
          if (currentUser?.playerId === editingPlayerId && pl.email) setCurrentUser(prev => ({ ...prev, email: pl.email }));
        } else { const e = await res.json().catch(() => ({})); showSnack(e.message || 'Fehler beim Speichern.', 'error'); }
      } else {
        const res = await fetch('/api/players', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify(playerData),
        });
        if (res.ok) {
          const pl = await res.json();
          setPlayers(prev => [...prev, pl]);
          setPlayerDialog(false); resetPlayer();
          showSnack('Spieler erfolgreich hinzugefügt!');
          if (currentUser?.role === 'admin') fetchAdminUsers();
        } else { const e = await res.json().catch(() => ({})); showSnack(e.message || 'Fehler beim Speichern.', 'error'); }
      }
    } catch { showSnack('Fehler beim Speichern.', 'error'); }
  };

  const twoWeeksAgo = new Date(Date.now() - 14 * 86400000).toISOString().slice(0, 10);
  const filteredEvents = expandRecurringEvents(events)
    .filter(e => {
      const q = search.toLowerCase();
      return e.name?.toLowerCase().includes(q) || e.type?.toLowerCase().includes(q) || e.location?.toLowerCase().includes(q);
    })
    .filter(e => {
      if (activeFilters.length === 0) return true;
      const typeFilters = activeFilters.filter(f => ['training','turnier','aktivitaet'].includes(f));
      const otherFilters = activeFilters.filter(f => !['training','turnier','aktivitaet'].includes(f));
      const typeMatch = typeFilters.length === 0 || typeFilters.some(f => e.type === f);
      const otherMatch = otherFilters.every(f => {
        if (f === 'neu') return e.createdAt >= twoWeeksAgo;
        if (!currentUser) return false;
        const playerKey = currentUser.playerId ? `player-${currentUser.playerId}` : currentUser.email;
        if (f === 'zusage') return e.signups?.includes(playerKey);
        if (f === 'absage') return e.declines?.includes(playerKey);
        if (f === 'offen') return !e.signups?.includes(playerKey) && !e.declines?.includes(playerKey);
        return true;
      });
      return typeMatch && otherMatch;
    })
    .sort((a, b) => a.date > b.date ? 1 : -1);

  const eventGroups = groupEventsByDate(filteredEvents);
  const firstFutureGroupIdx = eventGroups.findIndex(g => g.key !== 'past');

  const isPrivilegedSearch = currentUser?.role === 'admin' || currentUser?.role === 'trainer';
  const activeSkillSort = parseSkillSort(playerSort);
  const filteredPlayers = players.filter(p => {
    const q = search.toLowerCase();
    const autoTeams = computeAutoTeam(p.birthyear, p.gender);
    if (q) {
      const publicMatch =
        `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) ||
        p.capNumber?.toString().includes(q) ||
        p.nickname?.toLowerCase().includes(q) ||
        p.primaryPosition?.toLowerCase().includes(q) ||
        [p.team, ...autoTeams].filter(Boolean).some(t => t.toLowerCase().includes(q));
      if (!publicMatch && !isPrivilegedSearch) return false;
      if (!publicMatch) {
        const privMatch =
          p.email?.toLowerCase().includes(q) || p.gender?.toLowerCase().includes(q) ||
          p.birthyear?.toString().includes(q) || p.secondaryPosition?.toLowerCase().includes(q) ||
          p.city?.toLowerCase().includes(q) || p.cityCode?.toString().includes(q) ||
          p.street?.toLowerCase().includes(q) || p.playerPassNumber?.toString().includes(q) ||
          p.refereePassNumber?.toString().includes(q) || (p.isReferee ? 'schiedsrichter' : '').includes(q);
        if (!privMatch) return false;
      }
    }
    if (activePlayerFilters.length === 0) return true;
    return activePlayerFilters.every(f => {
      if (f === 'schiri') return !!p.isReferee;
      if (f === 'noteam') return !p.team && autoTeams.length === 0;
      if (f.startsWith('pos1-')) return p.primaryPosition === f.slice(5);
      if (f.startsWith('pos2-')) return p.secondaryPosition === f.slice(5);
      if (f.startsWith('team-')) { const t = f.slice(5); return p.team === t || autoTeams.includes(t); }
      return true;
    });
  }).sort((a, b) => {
    if (playerSort === 'name-asc') return comparePlayerNamesAsc(a, b);
    if (playerSort === 'name-desc') return comparePlayerNamesAsc(b, a);
    if (playerSort === 'cap-asc') return (parseInt(a.capNumber) || 0) - (parseInt(b.capNumber) || 0) || comparePlayerNamesAsc(a, b);
    if (playerSort === 'cap-desc') return (parseInt(b.capNumber) || 0) - (parseInt(a.capNumber) || 0) || comparePlayerNamesAsc(a, b);
    if (playerSort === 'wert-asc' || playerSort === 'wert-desc') {
      const aValue = skillValue(a.skillStats);
      const bValue = skillValue(b.skillStats);
      if (aValue === null && bValue === null) return comparePlayerNamesAsc(a, b);
      if (aValue === null) return 1;
      if (bValue === null) return -1;
      return (playerSort === 'wert-asc' ? aValue - bValue : bValue - aValue) || comparePlayerNamesAsc(a, b);
    }
    if (activeSkillSort) {
      const aValue = getSkillStatValue(a, activeSkillSort.key);
      const bValue = getSkillStatValue(b, activeSkillSort.key);
      if (aValue === null && bValue === null) return comparePlayerNamesAsc(a, b);
      if (aValue === null) return 1;
      if (bValue === null) return -1;
      return (activeSkillSort.dir === 'asc' ? aValue - bValue : bValue - aValue) || comparePlayerNamesAsc(a, b);
    }
    return 0;
  });

  const canViewAllStats = currentUser?.role === 'admin' || currentUser?.role === 'trainer';
  const _now = new Date();
  const todayStr = `${_now.getFullYear()}-${String(_now.getMonth()+1).padStart(2,'0')}-${String(_now.getDate()).padStart(2,'0')}`;
  const statsEvents = expandRecurringEvents(events).filter(ev => {
    if (statsFilter === 'range') {
      if (statsStartDate && ev.date < statsStartDate) return false;
      if (statsEndDate && ev.date > statsEndDate) return false;
    }
    return true;
  });
  const getPlayerStats = (playerId) => {
    const pid = `player-${playerId}`;
    const trainingsTotal = statsEvents.filter(ev => ev.type === 'training').length;
    const tournamentsTotal = statsEvents.filter(ev => ev.type === 'turnier').length;
    const trainings = statsEvents.filter(ev => ev.type === 'training' && ev.signups?.includes(pid)).length;
    const tournaments = statsEvents.filter(ev => ev.type === 'turnier' && (parseInt(ev.gamesPlayed?.[pid]) || 0) > 0).length;
    const gamesPlayed = statsEvents.filter(ev => ev.type === 'turnier').reduce((s, ev) => s + (parseInt(ev.gamesPlayed?.[pid]) || 0), 0);
    const gamesRefereed = statsEvents.filter(ev => ev.type === 'turnier').reduce((s, ev) => s + (parseInt(ev.gamesRefereed?.[pid]) || 0), 0);
    const declines = statsEvents.filter(ev => ev.declines?.includes(pid)).length;
    const noResponse = statsEvents.filter(ev => {
      if (ev.date > todayStr) return false;
      const invited = ev.invitedPlayerIds || [];
      const isInvited = invited.length === 0 || invited.includes(playerId);
      return isInvited && !ev.signups?.includes(pid) && !ev.declines?.includes(pid);
    }).length;
    return { trainings, trainingsTotal, tournaments, tournamentsTotal, gamesPlayed, gamesRefereed, declines, noResponse };
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}>

        <AppBar position="fixed" elevation={0} sx={{ bgcolor: '#0D47A1', borderBottom: '1px solid rgba(255,255,255,0.1)', pt: 'env(safe-area-inset-top)' }}>
          <Toolbar sx={{ gap: { xs: 1, sm: 2 }, minHeight: { xs: 56, sm: 64 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: { xs: 0, sm: 2 }, flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', height: { xs: 36, sm: 44 } }}>
                <img src="/logo.svg" alt="UWR" style={{ height: '100%', filter: 'brightness(0) invert(1)', display: 'block' }} />
              </Box>
              <Typography variant="h6" fontWeight={700} color="white" sx={{ letterSpacing: 0.5, display: { xs: 'none', sm: 'block' } }}>
                UWR Manager
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
              <Tabs value={tab}
                onChange={(_, v) => { setTab(v); setSearch(''); setActiveFilters([]); setActivePlayerFilters([]); }}
                textColor="inherit" variant="scrollable" scrollButtons={false}
                TabIndicatorProps={{ style: { backgroundColor: '#90CAF9', height: 3 } }}>
                <Tab label={<Typography variant="body2" fontWeight={600} sx={{ display: { xs: 'none', sm: 'block' } }}>Termine</Typography>}
                  icon={<EventIcon />} iconPosition="start"
                  sx={{ color: 'rgba(255,255,255,0.8)', '&.Mui-selected': { color: '#fff' }, minHeight: { xs: 56, sm: 48 }, px: { xs: 1.5, sm: 3 } }} />
                <Tab label={<Typography variant="body2" fontWeight={600} sx={{ display: { xs: 'none', sm: 'block' } }}>Spieler</Typography>}
                  icon={<PersonIcon />} iconPosition="start"
                  sx={{ color: 'rgba(255,255,255,0.8)', '&.Mui-selected': { color: '#fff' }, minHeight: { xs: 56, sm: 48 }, px: { xs: 1.5, sm: 3 } }} />
                <Tab label={<Typography variant="body2" fontWeight={600} sx={{ display: { xs: 'none', sm: 'block' } }}>Links</Typography>}
                  icon={<LanguageIcon />} iconPosition="start"
                  sx={{ color: 'rgba(255,255,255,0.8)', '&.Mui-selected': { color: '#fff' }, minHeight: { xs: 56, sm: 48 }, px: { xs: 1.5, sm: 3 } }} />
                <Tab label={<Typography variant="body2" fontWeight={600} sx={{ display: { xs: 'none', sm: 'block' } }}>Statistik</Typography>}
                  icon={<QueryStatsIcon />} iconPosition="start"
                  sx={{ color: 'rgba(255,255,255,0.8)', '&.Mui-selected': { color: '#fff' }, minHeight: { xs: 56, sm: 48 }, px: { xs: 1.5, sm: 3 } }} />
                {currentUser?.role === 'admin' && (
                  <Tab label={<Typography variant="body2" fontWeight={600} sx={{ display: { xs: 'none', sm: 'block' } }}>Admin</Typography>}
                    icon={<AdminPanelSettingsIcon />} iconPosition="start"
                    sx={{ color: 'rgba(255,255,255,0.8)', '&.Mui-selected': { color: '#fff' }, minHeight: { xs: 56, sm: 48 }, px: { xs: 1.5, sm: 3 } }} />
                )}
              </Tabs>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', alignItems: 'flex-end' }}>
                <Typography variant="body2" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>{currentUser.name}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.2 }}>{currentUser.role}</Typography>
              </Box>
              <Tooltip title="Passwort ändern">
                <IconButton size="small" onClick={() => setChangePasswordOpen(true)}
                  sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                  <LockIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Abmelden">
                <IconButton size="small" onClick={handleSwitchRole}
                  sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
        </AppBar>

        <Box sx={{ mt: { xs: 'calc(56px + env(safe-area-inset-top))', sm: 'calc(64px + env(safe-area-inset-top))' }, flexGrow: 1, p: { xs: 1, sm: 3 }, pb: { xs: 10, sm: 3 }, maxWidth: 1100, width: '100%', mx: 'auto', overflowX: 'hidden' }}>

          <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: '1px solid #e0e7ef', borderRadius: 3, display: tab === 3 || tab === 4 ? 'none' : undefined }}>
            <TextField fullWidth variant="standard"
              placeholder={tab === 0 ? 'Termine suchen…' : 'Spieler suchen…'}
              value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{
                disableUnderline: true,
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary' }} /></InputAdornment>,
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}><CloseIcon fontSize="small" /></IconButton>
                  </InputAdornment>
                ),
              }} />
          </Paper>

          {tab === 0 && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2, overflowX: 'auto', flexWrap: { xs: 'nowrap', sm: 'wrap' }, pb: { xs: 0.5, sm: 0 }, '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
              <Chip label="Alle" clickable size="small" onClick={() => setActiveFilters([])}
                variant={activeFilters.length === 0 ? 'filled' : 'outlined'} color={activeFilters.length === 0 ? 'primary' : 'default'}
                sx={{ fontWeight: activeFilters.length === 0 ? 700 : 400, flexShrink: 0 }} />
              {FILTERS.map(f => (
                <Chip key={f.value} label={f.label} clickable size="small"
                  onClick={() => setActiveFilters(prev => {
                    if (prev.includes(f.value)) return prev.filter(x => x !== f.value);
                    const statusGroup = ['offen', 'zusage', 'absage'];
                    if (statusGroup.includes(f.value)) return [...prev.filter(x => !statusGroup.includes(x)), f.value];
                    return [...prev, f.value];
                  })}
                  variant={activeFilters.includes(f.value) ? 'filled' : 'outlined'} color={activeFilters.includes(f.value) ? 'primary' : 'default'}
                  sx={{ fontWeight: activeFilters.includes(f.value) ? 700 : 400, flexShrink: 0 }} />
              ))}
            </Box>
          )}

          {tab === 1 && (() => {
            const allTeams = [...new Set(players.flatMap(p => [p.team, ...computeAutoTeam(p.birthyear, p.gender)].filter(Boolean)))].sort();
            const togglePF = v => setActivePlayerFilters(prev => {
              if (prev.includes(v)) return prev.filter(x => x !== v);
              let filtered = prev;
              if (v.startsWith('pos2-')) { const pos = v.slice(5); filtered = prev.filter(x => x !== `pos1-${pos}`); }
              else if (v.startsWith('pos1-')) { const pos = v.slice(5); filtered = prev.filter(x => x !== `pos2-${pos}`); }
              const group = EXCLUSIVE_FILTER_GROUPS.find(g => g.includes(v));
              if (group) return [...filtered.filter(x => !group.includes(x)), v];
              return [...filtered, v];
            });
            return (
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', pb: { xs: 0.5, sm: 0 } }}>
                <Chip label="Alle" clickable size="small" onClick={() => setActivePlayerFilters([])}
                  variant={activePlayerFilters.length === 0 ? 'filled' : 'outlined'} color={activePlayerFilters.length === 0 ? 'primary' : 'default'}
                  sx={{ fontWeight: activePlayerFilters.length === 0 ? 700 : 400, flexShrink: 0 }} />
                {activePlayerFilters.length > 0 && (
                  <Chip label={`${activePlayerFilters.length} Filter aktiv`} size="small" onDelete={() => setActivePlayerFilters([])}
                    sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: '#E3F2FD', color: '#0D47A1', '& .MuiChip-deleteIcon': { fontSize: 14 }, flexShrink: 0 }} />
                )}
                {allTeams.map(t => (
                  <Chip key={`team-${t}`} label={t} clickable size="small" onClick={() => togglePF(`team-${t}`)}
                    variant={activePlayerFilters.includes(`team-${t}`) ? 'filled' : 'outlined'} color={activePlayerFilters.includes(`team-${t}`) ? 'secondary' : 'default'}
                    sx={{ fontWeight: activePlayerFilters.includes(`team-${t}`) ? 700 : 400, flexShrink: 0 }} />
                ))}
                {PLAYER_POS_FILTERS
                  .filter(f => !(f.value.startsWith('pos2-') && activePlayerFilters.some(af => af === 'pos1-' + f.value.slice(5))))
                  .map(f => (
                    <Chip key={f.value} label={f.label} clickable size="small" onClick={() => togglePF(f.value)}
                      variant={activePlayerFilters.includes(f.value) ? 'filled' : 'outlined'} color={activePlayerFilters.includes(f.value) ? 'primary' : 'default'}
                      sx={{ fontWeight: activePlayerFilters.includes(f.value) ? 700 : 400, flexShrink: 0 }} />
                  ))}
              </Box>
            );
          })()}

          <TabPanel value={tab} index={0}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, gap: 0.5 }}>
              <IconButton size="small" onClick={() => setEventView('list')}
                sx={{ color: eventView === 'list' ? '#0D47A1' : 'text.disabled', bgcolor: eventView === 'list' ? '#E3F2FD' : 'transparent', borderRadius: 1 }}>
                <ViewListIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => setEventView('calendar')}
                sx={{ color: eventView === 'calendar' ? '#0D47A1' : 'text.disabled', bgcolor: eventView === 'calendar' ? '#E3F2FD' : 'transparent', borderRadius: 1 }}>
                <CalendarViewMonthIcon fontSize="small" />
              </IconButton>
            </Box>
            {eventView === 'calendar' && (
              <CalendarView events={filteredEvents} calMonth={calMonth} setCalMonth={setCalMonth}
                setDetailEvent={openEventDetail}
                onDateClick={canCreate ? (dateStr) => {
                  setFormData({ ...emptyEvent, date: dateStr });
                  setEditingEventId(null); setReopenEventAfterEdit(null); setEventDialog(true);
                } : null} />
            )}
            {eventView === 'list' && (filteredEvents.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                <EventIcon sx={{ fontSize: 56, opacity: 0.25, mb: 1 }} />
                <Typography variant="body1">Keine Termine gefunden.</Typography>
                {canCreate && <Typography variant="body2">Klicke auf + um einen neuen Termin anzulegen.</Typography>}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {eventGroups.map((group, groupIdx) => {
                  const isPastGroup = group.key === 'past';
                  const isFirstFuture = groupIdx === firstFutureGroupIdx;
                  const PAST_LIMIT = 5;
                  const PAST_WEEKS = 4;
                  const fourWeeksAgo = new Date(); fourWeeksAgo.setDate(fourWeeksAgo.getDate() - PAST_WEEKS * 7);
                  const fourWeeksAgoStr = fourWeeksAgo.toISOString().slice(0, 10);
                  const sortedPast = isPastGroup ? [...group.events].sort((a, b) => b.date > a.date ? 1 : -1) : group.events;
                  const visibleEvents = isPastGroup && !expandPastAll
                    ? sortedPast.filter(e => e.date >= fourWeeksAgoStr).slice(0, PAST_LIMIT)
                    : sortedPast;
                  const hiddenCount = isPastGroup ? group.events.length - visibleEvents.length : 0;
                  return (
                    <Box key={group.key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1, sm: 1.5 }, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setCollapsedGroups(prev => prev.includes(group.key) ? prev.filter(k => k !== group.key) : [...prev, group.key])}>
                        <Typography variant="overline" fontWeight={900} sx={{ color: group.color, letterSpacing: 1.5, lineHeight: 1, fontSize: 11 }}>
                          {group.label}
                        </Typography>
                        <Chip label={group.events.length} size="small"
                          sx={{ bgcolor: group.color, color: '#fff', fontWeight: 700, height: 18, fontSize: 10, '& .MuiChip-label': { px: 0.75 } }} />
                        <Box sx={{ flex: 1, height: '1px', bgcolor: `${group.color}35` }} />
                        {collapsedGroups.includes(group.key) ? <ExpandMoreIcon sx={{ fontSize: 16, color: group.color }} /> : <ExpandLessIcon sx={{ fontSize: 16, color: group.color }} />}
                      </Box>
                      {!collapsedGroups.includes(group.key) && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.75, sm: 1.25 } }}>
                          {visibleEvents.map((event, evIdx) => {
                            const meta = getEventMeta(event.type);
                            const isNext = isFirstFuture && evIdx === 0;
                            const evDate = parseLocalDate(event.date);
                            const dayNum = evDate ? evDate.getDate() : '';
                            const monthShort = evDate ? MONTH_DE[evDate.getMonth()] : '';
                            const weekdayShort = evDate ? WEEKDAY_DE[evDate.getDay()] : '';
                            const timeLabel = event.startTime ? (event.endTime ? `${event.startTime} – ${event.endTime}` : event.startTime) : null;
                            return (
                              <Box key={event._key || event.id} sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, opacity: isPastGroup ? 0.55 : 1, cursor: 'pointer' }}
                                onClick={() => openEventDetail(event)}>
                                <Box sx={{ width: { xs: 38, sm: 52 }, flexShrink: 0, textAlign: 'center', pt: 0.5 }}>
                                  <Typography variant="caption" sx={{ display: 'block', lineHeight: 1, color: 'text.secondary', fontWeight: 600, mb: '2px' }}>{weekdayShort}</Typography>
                                  <Typography fontWeight={900} sx={{ display: 'block', lineHeight: 1, fontSize: { xs: 22, sm: 30 }, color: group.key === 'heute' ? '#E65100' : 'text.primary' }}>{dayNum}</Typography>
                                  <Typography variant="caption" sx={{ display: 'block', lineHeight: 1, color: 'text.secondary', mt: '2px' }}>{monthShort}</Typography>
                                </Box>
                                <Card sx={{ flex: 1, borderLeft: `4px solid ${meta.color}`, borderRadius: 2, transition: 'box-shadow 0.15s', outline: isNext ? `2px solid ${meta.color}` : 'none', outlineOffset: 1, '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.13)' } }}>
                                  <CardContent sx={{ py: { xs: '7px !important', sm: '10px !important' }, px: { xs: 1, sm: 2 } }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
                                          <Chip icon={meta.icon} label={meta.label} size="small" sx={{ bgcolor: `${meta.color}18`, color: meta.color, fontWeight: 600, fontSize: 11, height: 22 }} />
                                          {event.recurring && <Chip icon={<RepeatIcon />} label="Wdh." size="small" variant="outlined" color="secondary" sx={{ height: 20, fontSize: 10 }} />}
                                          {isNext && <Chip label="Nächster" size="small" sx={{ height: 20, fontSize: 10, bgcolor: `${meta.color}22`, color: meta.color, fontWeight: 700 }} />}
                                        </Box>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{event.name}</Typography>
                                        {event.location && (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                            <LocationIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                                            <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{event.location}</Typography>
                                          </Box>
                                        )}
                                      </Box>
                                      <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
                                        {timeLabel && <Typography variant="body2" fontWeight={800} sx={{ color: meta.color, whiteSpace: 'nowrap', letterSpacing: -0.3 }}>{timeLabel}</Typography>}
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', mt: timeLabel ? 0.5 : 0, flexWrap: 'wrap' }}>
                                          {(event.type === 'training' || event.type === 'turnier') && hasLineupAssignments(event.lineup) && (
                                            <Chip icon={<LineupIcon sx={{ fontSize: '11px !important' }} />}
                                              label={event.lineupPublished ? 'Aufst. ✓' : 'Aufst.'} size="small"
                                              sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: event.lineupPublished ? '#E8F5E9' : '#E3F2FD', color: event.lineupPublished ? '#2E7D32' : '#0D47A1', '& .MuiChip-icon': { ml: '4px', mr: '-2px' } }} />
                                          )}
                                        </Box>
                                      </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.75 }}>
                                      <Box sx={{ display: 'flex', gap: 0.75 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                          <CheckCircleIcon sx={{ fontSize: 12, color: '#2E7D32' }} />
                                          {(() => {
                                            const n = event.signups?.length || 0;
                                            const color = n >= 10 ? '#2E7D32' : n >= 6 ? '#F57F17' : '#E65100';
                                            return <Typography variant="body2" fontWeight={700} sx={{ color, lineHeight: 1 }}>{n}</Typography>;
                                          })()}
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                          <CancelIcon sx={{ fontSize: 12, color: '#C62828' }} />
                                          <Typography variant="caption" color="text.secondary">{event.declines?.length || 0}</Typography>
                                        </Box>
                                      </Box>
                                    </Box>
                                  </CardContent>
                                </Card>
                              </Box>
                            );
                          })}
                          {isPastGroup && !expandPastAll && hiddenCount > 0 && (
                            <Box sx={{ textAlign: 'center', pt: 0.5 }}>
                              <Button size="small" onClick={() => setExpandPastAll(true)} sx={{ color: group.color, textTransform: 'none', fontSize: 12 }}>
                                Alle {group.events.length} vergangenen anzeigen
                              </Button>
                            </Box>
                          )}
                          {isPastGroup && expandPastAll && (
                            <Box sx={{ textAlign: 'center', pt: 0.5 }}>
                              <Button size="small" onClick={() => setExpandPastAll(false)} sx={{ color: group.color, textTransform: 'none', fontSize: 12 }}>
                                Weniger anzeigen
                              </Button>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel id="player-skill-sort-label">Skill-Sort</InputLabel>
                <Select
                  labelId="player-skill-sort-label"
                  label="Skill-Sort"
                  value={activeSkillSort?.key || ''}
                  onChange={(event) => setPlayerSort(event.target.value ? `skill-${event.target.value}-${activeSkillSort?.dir || 'desc'}` : 'name-asc')}
                >
                  <MenuItem value="">Kein Skill-Sort</MenuItem>
                  {SKILL_FIELDS.map(({ key, label }) => (
                    <MenuItem key={key} value={key}>{label}</MenuItem>
                  ))}
                </Select>
                </FormControl>
                <Chip
                  label={activeSkillSort?.dir === 'asc' ? 'Skill↑' : 'Skill↓'}
                  size="small"
                  clickable={!!activeSkillSort}
                  onClick={() => activeSkillSort && setPlayerSort(`skill-${activeSkillSort.key}-${activeSkillSort.dir === 'asc' ? 'desc' : 'asc'}`)}
                  variant={activeSkillSort ? 'filled' : 'outlined'}
                  color={activeSkillSort ? 'primary' : 'default'}
                  sx={{ fontWeight: activeSkillSort ? 700 : 400, fontSize: 11, height: 32, opacity: activeSkillSort ? 1 : 0.5 }}
                />
              </Box>
              {[{v:'name-asc',l:'A→Z'},{v:'name-desc',l:'Z→A'},{v:'cap-asc',l:'#↑'},{v:'cap-desc',l:'#↓'},{v:'wert-asc',l:'Wert↑'},{v:'wert-desc',l:'Wert↓'}].map(opt => (
                <Chip key={opt.v} label={opt.l} size="small" clickable onClick={() => setPlayerSort(opt.v)}
                  variant={playerSort === opt.v ? 'filled' : 'outlined'} color={playerSort === opt.v ? 'primary' : 'default'}
                  sx={{ fontWeight: playerSort === opt.v ? 700 : 400, fontSize: 11, height: 24 }} />
              ))}
            </Box>
            {filteredPlayers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                <PersonIcon sx={{ fontSize: 56, opacity: 0.25, mb: 1 }} />
                <Typography variant="body1">Noch keine Spieler registriert.</Typography>
                {canManagePlayers && <Typography variant="body2">Spieler können im Admin-Tab hinzugefügt werden.</Typography>}
              </Box>
            ) : (
              <Paper elevation={0} sx={{ border: '1px solid #e0e7ef', borderRadius: 3, overflow: 'hidden' }}>
                <List disablePadding>
                  {filteredPlayers.map((player, i) => (
                    <React.Fragment key={player.id}>
                      {i > 0 && <Divider />}
                      <ListItem onClick={() => {
                        if (currentUser?.role === 'admin' || currentUser?.role === 'trainer') {
                          setPlayerData({ ...player }); setEditingPlayerId(player.id); setSkillsOnlyEdit(true); setPlayerDialog(true);
                        } else { setDetailPlayer(player); }
                      }}
                      sx={{ py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#f5f8ff' } }}>
                        {(() => {
                          const value = skillValue(player.skillStats);
                          const top3 = topSkills(player.skillStats, 3);
                          const allSkills = topSkills(player.skillStats, SKILL_FIELDS.length);
                          return (
                            <>
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: '#0D47A1', fontWeight: 700, fontSize: { xs: '0.68rem', sm: '0.8rem' } }}>
                                  {capLabel(player.capNumber)}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{player.firstName} {player.lastName}</Typography>
                                    {player.primaryPosition && <Chip label={player.primaryPosition} size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#F3E5F5', color: '#6A1B9A', fontWeight: 600 }} />}
                                    {player.nickname && <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>„{player.nickname}"</Typography>}
                                  </Box>
                                }
                                secondary={
                                  <>
                                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'nowrap', overflow: 'auto', pb: 0.5, '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
                                      {player.team && <Chip label={player.team} size="small" sx={{ height: 20, fontSize: 11, bgcolor: '#E3F2FD', color: '#0D47A1' }} />}
                                      {computeAutoTeam(player.birthyear, player.gender).map(t => (
                                        <Chip key={t} label={t} size="small" sx={{ height: 20, fontSize: 11, bgcolor: '#E3F2FD', color: '#0D47A1' }} />
                                      ))}
                                      {player.isReferee && (
                                        <Chip icon={<GavelIcon sx={{ fontSize: '12px !important' }} />} label="Schiri" size="small"
                                          sx={{ height: 20, fontSize: 11, bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, '& .MuiChip-icon': { ml: '4px' } }} />
                                      )}
                                      {player.secondaryPosition && (
                                        <Chip label={`${player.secondaryPosition} (2.)`} size="small" variant="outlined"
                                          sx={{ height: 20, fontSize: 11, borderColor: '#6A1B9A', color: '#6A1B9A', opacity: 0.65 }} />
                                      )}
                                    </Box>
                                    {(currentUser?.role === 'admin' || currentUser?.role === 'trainer') && (
                                      <>
                                        <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.5, mt: 0.25, flexWrap: 'nowrap', overflow: 'auto', pb: 0.25, '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
                                          {value !== null && <Chip label={`Wert ${value.toFixed(1)}`} size="small" sx={{ height: 20, fontSize: 11, bgcolor: '#FFF8E1', color: '#E65100', fontWeight: 700 }} />}
                                          {top3.map(s => (
                                            <Chip key={s.key} label={`${s.label}: ${s.value}`} size="small" variant="outlined"
                                              sx={{ height: 20, fontSize: 11, borderColor: '#90A4AE', color: '#455A64', bgcolor: '#fff' }} />
                                          ))}
                                        </Box>
                                        <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
                                          {value !== null && <Chip label={`Wert ${value.toFixed(1)}`} size="small" sx={{ height: 20, fontSize: 11, bgcolor: '#FFF8E1', color: '#E65100', fontWeight: 700 }} />}
                                          {allSkills.map(s => (
                                            <Chip key={s.key} label={`${s.label}: ${s.value}`} size="small" variant="outlined"
                                              sx={{ height: 20, fontSize: 11, borderColor: '#CFD8DC', color: '#37474F', bgcolor: '#fff' }} />
                                          ))}
                                        </Box>
                                      </>
                                    )}
                                  </>
                                }
                              />
                            </>
                          );
                        })()}
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            )}
          </TabPanel>

          <TabPanel value={tab} index={2}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {UWR_LINKS.map(section => (
                <Box key={section.category}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Typography variant="overline" fontWeight={900} sx={{ color: '#0D47A1', letterSpacing: 1.5, lineHeight: 1, fontSize: 11 }}>
                      {section.category}
                    </Typography>
                    <Box sx={{ flex: 1, height: '1px', bgcolor: '#0D47A122' }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {section.links.map(link => (
                      <Card key={link.url} elevation={0} sx={{ border: '1px solid #e0e7ef', borderRadius: 2, '&:hover': { borderColor: '#0D47A1', boxShadow: '0 2px 12px rgba(13,71,161,0.1)' }, transition: 'all 0.15s' }}>
                        <CardContent sx={{ py: '12px !important', px: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <LanguageIcon sx={{ color: '#0D47A1', opacity: 0.6, flexShrink: 0 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography fontWeight={700} variant="body2">{link.label}</Typography>
                            <Typography variant="caption" color="text.secondary">{link.desc}</Typography>
                          </Box>
                          <IconButton size="small" component="a" href={link.url} target="_blank" rel="noopener noreferrer" sx={{ color: '#0D47A1', flexShrink: 0 }}>
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </TabPanel>

          <TabPanel value={tab} index={3}>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              {[{ v: 'all', l: 'Alle Zeit' }, { v: 'range', l: 'Zeitraum' }].map(opt => (
                <Chip key={opt.v} label={opt.l} size="small" clickable onClick={() => setStatsFilter(opt.v)}
                  variant={statsFilter === opt.v ? 'filled' : 'outlined'} color={statsFilter === opt.v ? 'primary' : 'default'}
                  sx={{ fontWeight: statsFilter === opt.v ? 700 : 400 }} />
              ))}
              {statsFilter === 'range' && (
                <Box sx={{ display: 'flex', gap: 1, width: '100%', flexWrap: 'wrap' }}>
                  <TextField size="small" type="date" label="Von" InputLabelProps={{ shrink: true }}
                    value={statsStartDate} onChange={e => setStatsStartDate(e.target.value)} sx={{ width: { xs: '100%', sm: 150 } }} />
                  <TextField size="small" type="date" label="Bis" InputLabelProps={{ shrink: true }}
                    value={statsEndDate} onChange={e => setStatsEndDate(e.target.value)} sx={{ width: { xs: '100%', sm: 150 } }} />
                </Box>
              )}
            </Box>
            {canViewAllStats ? (
              <>
                <Paper elevation={0} sx={{ p: 1, mb: 1.5, border: '1px solid #e0e7ef', borderRadius: 2 }}>
                  <TextField fullWidth variant="standard" placeholder="Spieler suchen…"
                    value={statsSearch} onChange={e => setStatsSearch(e.target.value)}
                    InputProps={{
                      disableUnderline: true,
                      startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary' }} /></InputAdornment>,
                      endAdornment: statsSearch ? <InputAdornment position="end"><IconButton size="small" onClick={() => setStatsSearch('')}><CloseIcon fontSize="small" /></IconButton></InputAdornment> : null,
                    }} />
                </Paper>
                <Box sx={{ overflowX: 'auto', border: '1px solid #e0e7ef', borderRadius: 3 }}>
                  <Paper elevation={0} sx={{ border: 'none', borderRadius: 0, overflow: 'visible', minWidth: '100%' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '220px repeat(8, 72px)', sm: '1fr repeat(8, 72px)' }, bgcolor: '#f0f4f8', borderBottom: '1px solid #e0e7ef', px: 2, py: 1.5, position: 'sticky', top: 0, zIndex: 10 }}>
                      {[
                        { col: 'name', label: 'Spieler', align: 'left' },
                        { col: 'trainings', label: 'Trainings', align: 'center' },
                        { col: 'trainingsRate', label: 'Quote\u00a0%', align: 'center' },
                        { col: 'tournaments', label: 'Turniere', align: 'center' },
                        { col: 'tournamentsRate', label: 'Quote\u00a0%', align: 'center' },
                        { col: 'gamesPlayed', label: 'Spiele', align: 'center' },
                        { col: 'gamesRefereed', label: 'Schiri', align: 'center' },
                        { col: 'declines', label: 'Absagen', align: 'center' },
                        { col: 'noResponse', label: 'K.\u00a0Antw.', align: 'center' },
                      ].map(({ col, label, align }) => {
                        const active = statsSort.col === col;
                        return (
                          <Box key={col} onClick={() => setStatsSort(prev => prev.col === col ? { col, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { col, dir: col === 'name' ? 'asc' : 'desc' })}
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: align === 'left' ? 'flex-start' : 'center', cursor: 'pointer', userSelect: 'none', gap: 0.25, minHeight: 40, color: active ? '#0D47A1' : 'text.secondary', '&:hover': { color: '#0D47A1' }, ...(col === 'name' && { position: 'sticky', left: 0, bgcolor: '#f0f4f8', zIndex: 11 }) }}>
                            <Typography variant="caption" fontWeight={active ? 800 : 700} sx={{ lineHeight: 1.4 }}>{label}</Typography>
                            {active && <Typography variant="caption" fontWeight={800} sx={{ lineHeight: 1.4 }}>{statsSort.dir === 'desc' ? '\u2193' : '\u2191'}</Typography>}
                          </Box>
                        );
                      })}
                    </Box>
                    {[...players].filter(p => {
                      if (!statsSearch) return true;
                      const q = statsSearch.toLowerCase();
                      return `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) || p.capNumber?.toString().includes(q);
                    }).sort((a, b) => {
                      const sa = getPlayerStats(a.id);
                      const sb = getPlayerStats(b.id);
                      const vals = {
                        name: (a.lastName || '').localeCompare(b.lastName || '', 'de') || (a.firstName || '').localeCompare(b.firstName || '', 'de'),
                        trainings: sa.trainings - sb.trainings,
                        trainingsRate: (sa.trainingsTotal > 0 ? sa.trainings / sa.trainingsTotal : 0) - (sb.trainingsTotal > 0 ? sb.trainings / sb.trainingsTotal : 0),
                        tournaments: sa.tournaments - sb.tournaments,
                        tournamentsRate: (sa.tournamentsTotal > 0 ? sa.tournaments / sa.tournamentsTotal : 0) - (sb.tournamentsTotal > 0 ? sb.tournaments / sb.tournamentsTotal : 0),
                        gamesPlayed: sa.gamesPlayed - sb.gamesPlayed,
                        gamesRefereed: sa.gamesRefereed - sb.gamesRefereed,
                        declines: sa.declines - sb.declines,
                        noResponse: sa.noResponse - sb.noResponse,
                      };
                      const cmp = typeof vals[statsSort.col] === 'number' ? vals[statsSort.col] : (vals[statsSort.col] ?? vals.name);
                      return statsSort.dir === 'desc' ? -cmp : cmp;
                    }).map((player, i) => {
                      const st = getPlayerStats(player.id);
                      const tRate = st.trainingsTotal > 0 ? `${Math.round(st.trainings / st.trainingsTotal * 100)}%` : '\u2013';
                      const tournRate = st.tournamentsTotal > 0 ? `${Math.round(st.tournaments / st.tournamentsTotal * 100)}%` : '\u2013';
                      return (
                        <React.Fragment key={player.id}>
                          {i > 0 && <Divider />}
                          <Box sx={{ '--row-bg': '#fff', display: 'grid', gridTemplateColumns: { xs: '220px repeat(8, 72px)', sm: '1fr repeat(8, 72px)' }, px: 2, py: 1, alignItems: 'center', bgcolor: 'var(--row-bg)', '&:hover': { '--row-bg': '#f5f8ff' } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, position: 'sticky', left: 0, bgcolor: 'var(--row-bg)', zIndex: 9, alignSelf: 'stretch', pr: 1 }}>
                              <Avatar sx={{ bgcolor: '#0D47A1', width: 28, height: 28, fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                                {player.firstName?.[0]}{player.lastName?.[0]}
                              </Avatar>
                              <Typography variant="body2" fontWeight={600} sx={{ overflow: { xs: 'visible', sm: 'hidden' }, textOverflow: { xs: 'clip', sm: 'ellipsis' }, whiteSpace: { xs: 'normal', sm: 'nowrap' }, lineHeight: 1.25 }}>
                                {player.firstName} {player.lastName}
                              </Typography>
                            </Box>
                            {[st.trainings, tRate, st.tournaments, tournRate, st.gamesPlayed, st.gamesRefereed, st.declines, st.noResponse].map((val, vi) => (
                              <Typography key={vi} variant="body2" textAlign="center" fontWeight={[0, 2].includes(vi) ? 600 : 400} color={[0, 2].includes(vi) ? 'text.primary' : 'text.secondary'}>{val}</Typography>
                            ))}
                          </Box>
                        </React.Fragment>
                      );
                    })}
                  </Paper>
                </Box>
              </>
            ) : (() => {
              const st = getPlayerStats(currentUser.playerId);
              const tRate = st.trainingsTotal > 0 ? Math.round(st.trainings / st.trainingsTotal * 100) : null;
              const tournRate = st.tournamentsTotal > 0 ? Math.round(st.tournaments / st.tournamentsTotal * 100) : null;
              return (
                <Paper elevation={0} sx={{ border: '1px solid #e0e7ef', borderRadius: 3, p: 3 }}>
                  <Typography variant="h6" fontWeight={700} gutterBottom>Meine Statistik</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 2 }}>
                    {[
                      { label: 'Trainings', value: st.trainings, sub: `von ${st.trainingsTotal}${tRate !== null ? ` (${tRate}%)` : ''}` },
                      { label: 'Turniere gespielt', value: st.tournaments, sub: `von ${st.tournamentsTotal}${tournRate !== null ? ` (${tournRate}%)` : ''}` },
                      { label: 'Spiele', value: st.gamesPlayed, sub: 'Feldspieler-Einsätze' },
                      { label: 'Schiri-Spiele', value: st.gamesRefereed, sub: 'Schiedsrichtereinsätze' },
                      { label: 'Abgesagt', value: st.declines, sub: 'Absagen gesamt' },
                      { label: 'Keine Antwort', value: st.noResponse, sub: 'eingeladen, keine Reaktion' },
                    ].map(item => (
                      <Box key={item.label} sx={{ bgcolor: '#f0f4f8', borderRadius: 2, p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight={900} color="primary">{item.value}</Typography>
                        <Typography variant="body2" fontWeight={700}>{item.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.sub}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              );
            })()}
          </TabPanel>

          <TabPanel value={tab} index={4}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AdminPanelSettingsIcon sx={{ color: '#0D47A1' }} /> Benutzerverwaltung
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="outlined" color="warning" onClick={() => {
                  if (window.confirm('Alle Statistikwerte (Trainings/Turniere/Spiele/Absagen) wirklich zurücksetzen?')) handleResetAllPlayerStatistics();
                }}>
                  Alle Statistikwerte zurücksetzen
                </Button>
                <Button variant="outlined" color="secondary" onClick={() => {
                  if (window.confirm('Alle Spieler-Skillwerte wirklich zurücksetzen?')) handleResetAllPlayerSkillStats();
                }}>
                  Alle Skillwerte zurücksetzen
                </Button>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingPlayerId(null); setPlayerDialog(true); }}>
                  Neuer Spieler
                </Button>
              </Box>
            </Box>
            {adminLoading ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Lade…</Typography>
            ) : (
              <Paper elevation={0} sx={{ border: '1px solid #e0e7ef', borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                  <Box sx={{ minWidth: 980 }}>
                    {/* Sortable header */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '160px 60px 200px 130px 90px 160px 180px', px: 2, py: 1, bgcolor: '#f0f4f8', borderBottom: '1px solid #e0e7ef', position: 'sticky', top: 0, zIndex: 10 }}>
                      {[
                        { col: 'name', label: 'Name', sortable: true },
                        { col: 'cap', label: 'Nr.', sortable: true },
                        { col: 'email', label: 'E-Mail', sortable: true },
                        { col: 'role', label: 'Rolle', sortable: true },
                        { col: 'status', label: 'Status', sortable: true },
                        { col: 'lastLogin', label: 'Letzter Login', sortable: true },
                        { col: 'actions', label: 'Aktionen', sortable: false },
                      ].map(({ col, label, sortable }) => {
                        const active = adminSort.col === col;
                        return (
                          <Box key={col}
                            onClick={sortable ? () => setAdminSort(prev => prev.col === col ? { col, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { col, dir: col === 'lastLogin' ? 'desc' : 'asc' }) : undefined}
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.25, cursor: sortable ? 'pointer' : 'default', userSelect: 'none', color: active ? '#0D47A1' : 'text.secondary', '&:hover': sortable ? { color: '#0D47A1' } : {}, ...(col === 'name' && { position: 'sticky', left: 0, bgcolor: '#f0f4f8', zIndex: 11 }) }}>
                            <Typography variant="caption" fontWeight={active ? 800 : 700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
                            {active && sortable && <Typography variant="caption" fontWeight={800}>{adminSort.dir === 'desc' ? '↓' : '↑'}</Typography>}
                          </Box>
                        );
                      })}
                    </Box>
                    {adminUsers.length === 0 && <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>Keine Benutzer gefunden.</Typography>}
                    {[...adminUsers].sort((a, b) => {
                      const { col, dir } = adminSort;
                      let cmp = 0;
                      if (col === 'name') cmp = (a.name || a.email).localeCompare(b.name || b.email, 'de');
                      else if (col === 'cap') {
                        const capA = parseInt(a.capNumber, 10);
                        const capB = parseInt(b.capNumber, 10);
                        cmp = (Number.isFinite(capA) ? capA : 9999) - (Number.isFinite(capB) ? capB : 9999);
                      }
                      else if (col === 'email') cmp = (a.email || '').localeCompare(b.email || '', 'de');
                      else if (col === 'role') cmp = (a.role || '').localeCompare(b.role || '', 'de');
                      else if (col === 'status') cmp = (a.hasPassword ? 1 : 0) - (b.hasPassword ? 1 : 0);
                      else if (col === 'lastLogin') cmp = (a.lastLogin || '').localeCompare(b.lastLogin || '');
                      return dir === 'desc' ? -cmp : cmp;
                    }).map((u, i) => {
                      const isProtected = u.email === 'nbertsch85@googlemail.com';
                      const player = players.find(p => p.id === u.playerId);
                      const lastLoginStr = u.lastLogin
                        ? new Date(u.lastLogin).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) + '\u00a0' + new Date(u.lastLogin).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
                        : '–';
                      return (
                        <React.Fragment key={u.email}>
                          {i > 0 && <Divider />}
                          <Box sx={{ '--row-bg': '#fff', display: 'grid', gridTemplateColumns: '160px 60px 200px 130px 90px 160px 180px', px: 2, py: 1.25, alignItems: 'center', bgcolor: 'var(--row-bg)', '&:hover': { '--row-bg': '#f8f9ff' } }}>
                            <Box sx={{ position: 'sticky', left: 0, bgcolor: 'var(--row-bg)', zIndex: 9, pr: 1, alignSelf: 'stretch', display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>{u.name}</Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">{u.capNumber == null || String(u.capNumber).trim() === '' ? 'N/A' : u.capNumber}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', pr: 1 }}>{u.email}</Typography>
                            <FormControl size="small" disabled={isProtected}>
                              <Select value={u.role} onChange={e => handleRoleChange(u.email, e.target.value)} sx={{ fontSize: 13 }}>
                                <MenuItem value="player">Spieler</MenuItem>
                                <MenuItem value="trainer">Trainer</MenuItem>
                                <MenuItem value="admin">Admin</MenuItem>
                              </Select>
                            </FormControl>
                            <Chip size="small" label={u.hasPassword ? 'Aktiv' : 'Gesperrt'}
                              sx={{ fontWeight: 700, fontSize: 11, bgcolor: u.hasPassword ? '#e8f5e9' : '#fce4ec', color: u.hasPassword ? '#2e7d32' : '#c62828' }} />
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{lastLoginStr}</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                              {player && (
                                <Tooltip title="Spielerdaten bearbeiten">
                                  <IconButton size="small" sx={{ color: '#0D47A1' }}
                                    onClick={() => { setPlayerData({ ...player }); setEditingPlayerId(player.id); setPlayerDialog(true); }}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {player && (
                                <Tooltip title="Statistik zurücksetzen">
                                  <IconButton size="small" color="warning"
                                    onClick={() => {
                                      if (window.confirm(`Statistik von ${u.name} zurücksetzen?`)) handleResetPlayerStatistics(player.id);
                                    }}>
                                    <LineupIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {player && (
                                <Tooltip title="Skillwerte zurücksetzen">
                                  <IconButton size="small" color="warning"
                                    onClick={() => {
                                      if (window.confirm(`Skillwerte von ${u.name} zurücksetzen?`)) handleResetPlayerSkillStats(player.id);
                                    }}>
                                    <QueryStatsIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {player && (
                                <Tooltip title="Spieler löschen">
                                  <IconButton size="small" color="error"
                                    onClick={() => setDeleteConfirm({ open: true, type: 'player', item: player })}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Button size="small" variant="outlined" color={u.hasPassword ? 'inherit' : 'primary'}
                                onClick={() => setSetPwdDialog({
                                  open: true,
                                  playerId: u.playerId ?? player?.id,
                                  playerName: u.name,
                                  firstName: player?.firstName || (u.name || '').trim().split(/\s+/)[0] || '',
                                  loginEmail: u.email,
                                  requireEmail: !u.email || u.email.endsWith('@uwr.local'),
                                })}
                                sx={{ fontSize: 11, px: 1, textTransform: 'none', flexShrink: 0 }}>
                                {u.hasPassword ? 'PW ändern' : 'PW setzen'}
                              </Button>
                            </Box>
                          </Box>
                        </React.Fragment>
                      );
                    })}
                  </Box>
                </Box>
              </Paper>
            )}
          </TabPanel>
        </Box>

        {(tab === 0 && canCreate) && (
          <Fab color="primary"
            onClick={() => {
              if (tab === 0) {
                setFormData({ ...emptyEvent, type: currentUser?.role === 'Player' ? 'aktivitaet' : 'training' });
                setEditingEventId(null); setReopenEventAfterEdit(null); setEventDialog(true);
              } else { setEditingPlayerId(null); setPlayerDialog(true); }
            }}
            sx={{ position: 'fixed', bottom: { xs: 24, sm: 28 }, right: { xs: 20, sm: 28 }, boxShadow: '0 4px 20px rgba(13,71,161,0.4)' }}>
            <AddIcon />
          </Fab>
        )}

        {/* Event Create/Edit Dialog */}
        <Dialog open={eventDialog} onClose={closeEventDialogAndMaybeReopen} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
            {editingEventId ? 'Termin bearbeiten' : 'Neuen Termin erstellen'}
            <IconButton onClick={closeEventDialogAndMaybeReopen} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
            <TextField label="Name *" fullWidth value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
            <FormControl fullWidth>
              <InputLabel>Typ *</InputLabel>
              <Select label="Typ *" value={formData.type} disabled={currentUser?.role === 'Player'}
                onChange={e => setFormData({ ...formData, type: e.target.value })}>
                {(currentUser?.role === 'Player' ? EVENT_TYPES.filter(t => t.value === 'aktivitaet') : EVENT_TYPES).map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Datum *" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} />
            <FormControlLabel
              control={<Checkbox checked={!!formData.fullDay} onChange={e => setFormData({ ...formData, fullDay: e.target.checked })} />}
              label={<Typography variant="body2">Ganztägig</Typography>} />
            {!formData.fullDay && (
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <TextField label="Startzeit" type="time" fullWidth InputLabelProps={{ shrink: true }}
                  value={formData.startTime || ''} onChange={e => setFormData({ ...formData, startTime: e.target.value })} />
                <TextField label="Endzeit" type="time" fullWidth InputLabelProps={{ shrink: true }}
                  value={formData.endTime || ''} onChange={e => setFormData({ ...formData, endTime: e.target.value })} />
              </Box>
            )}
            {formData.type !== 'training' && (
              <TextField label="Enddatum (optional, bei mehrtägigen Terminen)" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={formData.endDate || ''} onChange={e => setFormData({ ...formData, endDate: e.target.value })} />
            )}
            <TextField label="Ort" fullWidth value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start"><LocationIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment> }} />
            <TextField label="Beschreibung" fullWidth multiline rows={2} value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })} />
            <Box sx={{ border: '1px solid #e0e7ef', borderRadius: 2, p: 1.5 }}>
              <FormControlLabel
                control={<Checkbox checked={formData.recurring} onChange={e => setFormData({ ...formData, recurring: e.target.checked })} />}
                label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><RepeatIcon fontSize="small" /><Typography variant="body2">Wiederkehrender Termin</Typography></Box>} />
              {formData.recurring && (
                <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  <Typography variant="caption" color="text.secondary">Wochentage</Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {WEEKDAYS.map(d => (
                      <Chip key={d.value} label={d.label} size="small" clickable
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          recurringDays: prev.recurringDays.includes(d.value) ? prev.recurringDays.filter(x => x !== d.value) : [...prev.recurringDays, d.value],
                        }))}
                        variant={formData.recurringDays.includes(d.value) ? 'filled' : 'outlined'}
                        color={formData.recurringDays.includes(d.value) ? 'primary' : 'default'} sx={{ fontWeight: 600 }} />
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary">Wiederholungsintervall</Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {RECURRING_INTERVALS.map(ri => (
                      <Chip key={ri.value} label={ri.label} size="small" clickable
                        onClick={() => setFormData({ ...formData, recurringInterval: ri.value })}
                        variant={formData.recurringInterval === ri.value ? 'filled' : 'outlined'}
                        color={formData.recurringInterval === ri.value ? 'primary' : 'default'} sx={{ fontWeight: 600 }} />
                    ))}
                  </Box>
                  <TextField label="Enddatum der Wiederholung (optional)" type="date" fullWidth InputLabelProps={{ shrink: true }}
                    value={formData.recurringEndDate || ''} onChange={e => setFormData({ ...formData, recurringEndDate: e.target.value })} />
                </Box>
              )}
            </Box>
            {(() => {
              const invIds = formData.invitedPlayerIds || [];
              const teams = [...new Set(players.map(p => p.team).filter(Boolean))];
              const allPlayerIds = players.map(p => p.id);
              return (
                <Box sx={{ border: '1px solid #e0e7ef', borderRadius: 2, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Eingeladene Spieler</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {invIds.length === 0 ? 'Leer = alle eingeladen' : `${invIds.length} von ${players.length} eingeladen`}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button size="small" variant="outlined" sx={{ fontSize: 11, px: 1, textTransform: 'none' }}
                        onClick={() => setFormData(prev => ({ ...prev, invitedPlayerIds: allPlayerIds }))}>Alle</Button>
                      <Button size="small" variant="outlined" sx={{ fontSize: 11, px: 1, textTransform: 'none' }}
                        onClick={() => setFormData(prev => ({ ...prev, invitedPlayerIds: [] }))}>Keine</Button>
                    </Box>
                  </Box>
                  {teams.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Teams</Typography>
                      <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                        {teams.map(t => {
                          const teamIds = players.filter(p => p.team === t).map(p => p.id);
                          const allSel = teamIds.length > 0 && teamIds.every(id => invIds.includes(id));
                          return (
                            <Chip key={t} label={t} size="small" clickable
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                invitedPlayerIds: allSel
                                  ? (prev.invitedPlayerIds || []).filter(id => !teamIds.includes(id))
                                  : [...new Set([...(prev.invitedPlayerIds || []), ...teamIds])],
                              }))}
                              variant={allSel ? 'filled' : 'outlined'} color={allSel ? 'secondary' : 'default'} sx={{ fontWeight: 600 }} />
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary">Einzeln</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                      {players.map(p => {
                        const sel = invIds.includes(p.id);
                        return (
                          <Chip key={p.id}
                            avatar={<Avatar sx={{ width: 20, height: 20, fontSize: 10 }}>{p.firstName[0]}{p.lastName[0]}</Avatar>}
                            label={`${p.firstName} ${p.lastName}`} size="small" clickable
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              invitedPlayerIds: sel ? (prev.invitedPlayerIds || []).filter(id => id !== p.id) : [...(prev.invitedPlayerIds || []), p.id],
                            }))}
                            variant={sel ? 'filled' : 'outlined'} color={sel ? 'primary' : 'default'} sx={{ fontSize: 11 }} />
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              );
            })()}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            {editingEventId && (
              <Button color="error" startIcon={<DeleteIcon />}
                onClick={() => {
                  setReopenEventAfterEdit(null); setEventDialog(false);
                  setDeleteConfirm({ open: true, type: 'event', item: { id: editingEventId, name: formData.name } });
                  setEditingEventId(null); setFormData(emptyEvent);
                }}
                sx={{ mr: 'auto' }}>Löschen</Button>
            )}
            <Button onClick={closeEventDialogAndMaybeReopen} color="inherit">Abbrechen</Button>
            <Button variant="contained" onClick={handleEventSubmit} disabled={!formData.name || !formData.date}>Speichern</Button>
          </DialogActions>
        </Dialog>

        {/* Player Create/Edit Dialog */}
        <Dialog open={playerDialog} onClose={() => { setPlayerDialog(false); setEditingPlayerId(null); resetPlayer(); setSkillsOnlyEdit(false); }} fullWidth maxWidth="md">
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
            {skillsOnlyEdit ? 'Skillbewertung bearbeiten' : editingPlayerId ? 'Spieler bearbeiten' : 'Spieler hinzufügen'}
            <IconButton onClick={() => { setPlayerDialog(false); setEditingPlayerId(null); resetPlayer(); setSkillsOnlyEdit(false); }} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
            {skillsOnlyEdit ? (
              <>
                <Typography variant="overline" color="text.secondary" sx={{ mb: -1 }}>Positionen</Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Primärposition</InputLabel>
                    <Select label="Primärposition" value={playerData.primaryPosition} onChange={e => setPlayerData({ ...playerData, primaryPosition: e.target.value })}>
                      <MenuItem value=""><em>– keine –</em></MenuItem>
                      {POSITIONS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Sekundärposition</InputLabel>
                    <Select label="Sekundärposition" value={playerData.secondaryPosition} onChange={e => setPlayerData({ ...playerData, secondaryPosition: e.target.value })}>
                      <MenuItem value=""><em>– keine –</em></MenuItem>
                      {POSITIONS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>
                <Divider />
                <Typography variant="overline" color="text.secondary" sx={{ mb: -1 }}>Skillbewertung (0 – 10)</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                  {SKILL_FIELDS.map(({ key, label }) => (
                    <Box key={key}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{label}: {playerData.skillStats?.[key] ?? 0}</Typography>
                      <Slider min={0} max={10} step={1} marks valueLabelDisplay="auto" size="small"
                        sx={{ touchAction: 'pan-y' }}
                        value={Number.isFinite(Number(playerData.skillStats?.[key])) ? Number(playerData.skillStats?.[key]) : 0}
                        onPointerDownCapture={e => handleSkillSliderPointerDown(key, e)}
                        onPointerUp={() => disarmSkillSliderTouch(key)}
                        onPointerCancel={() => disarmSkillSliderTouch(key)}
                        onBlur={() => disarmSkillSliderTouch(key)}
                        onChange={handleSkillSliderChange(key)} />
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <>
                <Typography variant="overline" color="text.secondary" sx={{ mb: -1 }}>Identifikation</Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                  <TextField label="Vorname *" fullWidth value={playerData.firstName} onChange={e => setPlayerData({ ...playerData, firstName: e.target.value })} />
                  <TextField label="Nachname *" fullWidth value={playerData.lastName} onChange={e => setPlayerData({ ...playerData, lastName: e.target.value })} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                  <TextField label="Kappennummer" fullWidth value={playerData.capNumber} onChange={e => setPlayerData({ ...playerData, capNumber: e.target.value })} />
                  <TextField label="Spitzname" fullWidth value={playerData.nickname} onChange={e => setPlayerData({ ...playerData, nickname: e.target.value })} />
                </Box>
                <Divider />
                <Typography variant="overline" color="text.secondary" sx={{ mb: -1 }}>Pers. Daten</Typography>
                <FormControl fullWidth>
                  <InputLabel>Geschlecht</InputLabel>
                  <Select label="Geschlecht" value={playerData.gender} onChange={e => setPlayerData({ ...playerData, gender: e.target.value })}>
                    <MenuItem value=""><em>– keine Angabe –</em></MenuItem>
                    <MenuItem value="männlich">männlich</MenuItem>
                    <MenuItem value="weiblich">weiblich</MenuItem>
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                  <TextField label="Geburtstag" type="number" inputProps={{ min: 1, max: 31 }} fullWidth value={playerData.birthday} onChange={e => setPlayerData({ ...playerData, birthday: e.target.value })} />
                  <TextField label="Geburtsmonat" type="number" inputProps={{ min: 1, max: 12 }} fullWidth value={playerData.birthmonth} onChange={e => setPlayerData({ ...playerData, birthmonth: e.target.value })} />
                  <TextField label="Geburtsjahr" type="number" inputProps={{ min: 1900, max: 2020 }} fullWidth value={playerData.birthyear} onChange={e => setPlayerData({ ...playerData, birthyear: e.target.value })} />
                </Box>
                <TextField label="E-Mail" fullWidth type="email" value={playerData.email} onChange={e => setPlayerData({ ...playerData, email: e.target.value })} />
                <Divider />
                <Typography variant="overline" color="text.secondary" sx={{ mb: -1 }}>Adresse</Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                  <TextField label="Straße" fullWidth value={playerData.street} onChange={e => setPlayerData({ ...playerData, street: e.target.value })} />
                  <TextField label="Hausnummer" sx={{ maxWidth: { sm: 120 } }} value={playerData.streetNumber} onChange={e => setPlayerData({ ...playerData, streetNumber: e.target.value })} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                  <TextField label="PLZ" sx={{ maxWidth: { sm: 120 } }} value={playerData.cityCode} onChange={e => setPlayerData({ ...playerData, cityCode: e.target.value })} />
                  <TextField label="Ort" fullWidth value={playerData.city} onChange={e => setPlayerData({ ...playerData, city: e.target.value })} />
                </Box>
                <Divider />
                <Typography variant="overline" color="text.secondary" sx={{ mb: -1 }}>Sportliche Angaben</Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Primärposition</InputLabel>
                    <Select label="Primärposition" value={playerData.primaryPosition} onChange={e => setPlayerData({ ...playerData, primaryPosition: e.target.value })}>
                      <MenuItem value=""><em>– keine –</em></MenuItem>
                      {POSITIONS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Sekundärposition</InputLabel>
                    <Select label="Sekundärposition" value={playerData.secondaryPosition} onChange={e => setPlayerData({ ...playerData, secondaryPosition: e.target.value })}>
                      <MenuItem value=""><em>– keine –</em></MenuItem>
                      {POSITIONS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>
                <FormControl fullWidth>
                  <InputLabel>Mannschaft</InputLabel>
                  <Select label="Mannschaft" value={playerData.team} onChange={e => setPlayerData({ ...playerData, team: e.target.value })}>
                    <MenuItem value=""><em>– keine –</em></MenuItem>
                    <MenuItem value="Erste Mannschaft">Erste Mannschaft</MenuItem>
                    <MenuItem value="Zweite Mannschaft">Zweite Mannschaft</MenuItem>
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'center' }}>
                  <TextField label="Spielerpass-Nr." fullWidth value={playerData.playerPassNumber} onChange={e => setPlayerData({ ...playerData, playerPassNumber: e.target.value })} />
                  <FormControlLabel control={<Checkbox checked={!!playerData.isReferee} onChange={e => setPlayerData({ ...playerData, isReferee: e.target.checked })} />} label="Schiedsrichter" sx={{ flexShrink: 0 }} />
                </Box>
                {playerData.isReferee && (
                  <TextField label="Schiedsrichterpass-Nr." fullWidth value={playerData.refereePassNumber} onChange={e => setPlayerData({ ...playerData, refereePassNumber: e.target.value })} />
                )}
                <Divider />
                <Typography variant="overline" color="text.secondary" sx={{ mb: -1 }}>Skillbewertung (0 – 10)</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                  {SKILL_FIELDS.map(({ key, label }) => (
                    <Box key={key}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{label}: {playerData.skillStats?.[key] ?? 0}</Typography>
                      <Slider min={0} max={10} step={1} marks valueLabelDisplay="auto" size="small"
                        sx={{ touchAction: 'pan-y' }}
                        value={Number.isFinite(Number(playerData.skillStats?.[key])) ? Number(playerData.skillStats?.[key]) : 0}
                        onPointerDownCapture={e => handleSkillSliderPointerDown(key, e)}
                        onPointerUp={() => disarmSkillSliderTouch(key)}
                        onPointerCancel={() => disarmSkillSliderTouch(key)}
                        onBlur={() => disarmSkillSliderTouch(key)}
                        onChange={handleSkillSliderChange(key)} />
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={() => { setPlayerDialog(false); setEditingPlayerId(null); resetPlayer(); setSkillsOnlyEdit(false); }} color="inherit">Abbrechen</Button>
            <Button variant="contained" onClick={handlePlayerSubmit} disabled={!skillsOnlyEdit && (!playerData.firstName || !playerData.lastName)}>Speichern</Button>
          </DialogActions>
        </Dialog>

        <EventDetailDialog
          event={detailEvent} open={!!detailEvent} onClose={() => setDetailEvent(null)}
          onSignup={handleSignup} onDecline={handleDecline} players={players}
          currentUserEmail={currentUser?.playerId ? `player-${currentUser.playerId}` : currentUser?.email}
          currentUserRole={currentUser?.role}
          onPlayerClick={(p) => { setReopenEventAfterPlayerDetail(detailEvent); setDetailEvent(null); setDetailPlayerMode('default'); setDetailPlayer(p); }}
          onEdit={(currentUser?.role === 'admin' || currentUser?.role === 'trainer') ? (ev) => {
            setReopenEventAfterEdit(ev); setFormData({ ...ev }); setEditingEventId(ev.id); setDetailEvent(null); setEventDialog(true);
          } : null}
          onDelete={(currentUser?.role === 'admin' || currentUser?.role === 'trainer')
            ? (ev) => { setDetailEvent(null); setDeleteConfirm({ open: true, type: 'event', item: ev }); }
            : null}
          onSignupFor={(currentUser?.role === 'admin' || currentUser?.role === 'trainer')
            || currentUser?.role === 'player'
            ? (playerEmail) => handleSignup(detailEvent?.id, detailEvent?.occurrenceDate || detailEvent?.date, playerEmail)
            : null}
          onDeclineFor={(currentUser?.role === 'admin' || currentUser?.role === 'trainer')
            || currentUser?.role === 'player'
            ? (playerEmail) => handleDecline(detailEvent?.id, detailEvent?.occurrenceDate || detailEvent?.date, playerEmail)
            : null}
          onOpenLineup={
            detailEvent && (detailEvent.type === 'training' || detailEvent.type === 'turnier') &&
            (currentUser?.role === 'admin' || currentUser?.role === 'trainer' ||
              (detailEvent.lineupPublished && hasLineupAssignments(detailEvent.lineup)))
              ? () => { setDetailEvent(null); setLineupDialogEvent(detailEvent); }
              : undefined
          }
        />
        <LineupDialog
          event={lineupDialogEvent} open={!!lineupDialogEvent} onClose={() => setLineupDialogEvent(null)}
          players={players}
          canEdit={currentUser?.role === 'admin' || currentUser?.role === 'trainer'}
          onSave={(lineupData) => handleSaveLineup(lineupDialogEvent?.id, lineupData, lineupDialogEvent?.recurring ? lineupDialogEvent?.date : undefined)}
          onPublish={(variantId) => handlePublishLineup(
            lineupDialogEvent?.id,
            lineupDialogEvent?.recurring ? lineupDialogEvent?.date : undefined,
            variantId
          )}
          onPlayerClick={(p) => { setDetailPlayerMode('lineup'); setDetailPlayer(p); }}
        />
        <PlayerDetailDialog
          player={detailPlayer} open={!!detailPlayer} onClose={closePlayerDetailAndMaybeReopenEvent}
          currentUserRole={currentUser?.role}
          mode={detailPlayerMode}
          onEdit={(currentUser?.role === 'admin' || currentUser?.role === 'trainer') ? openPlayerSkillEditor : null}
          isOwnProfile={currentUser?.playerId != null && currentUser.playerId === detailPlayer?.id}
          onEmailChange={async (newEmail) => {
            try {
              const res = await fetch(`/api/players/${detailPlayer.id}`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                body: JSON.stringify({ email: newEmail }),
              });
              const data = await res.json();
              if (!res.ok) return data.message || 'Fehler beim Speichern.';
              setPlayers(prev => prev.map(p => p.id === detailPlayer.id ? { ...p, email: newEmail } : p));
              setDetailPlayer(prev => ({ ...prev, email: newEmail }));
              if (currentUser?.playerId === detailPlayer?.id) setCurrentUser(prev => ({ ...prev, email: newEmail }));
              if (currentUser?.role === 'admin') fetchAdminUsers();
              showSnack('E-Mail gespeichert!');
              return null;
            } catch { return 'Netzwerkfehler.'; }
          }}
          onDelete={currentUser?.role === 'admin' ? (pl) => {
            setReopenEventAfterPlayerDetail(null);
            setDeleteConfirm({ open: true, type: 'player', item: pl });
            setDetailPlayer(null);
          } : null}
        />

        <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, type: null, item: null })} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>{deleteConfirm.type === 'event' ? 'Termin löschen?' : 'Spieler löschen?'}</DialogTitle>
          <DialogContent>
            {deleteConfirm.type === 'event' && deleteConfirm.item?.recurring ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography><strong>„{deleteConfirm.item?.name}"</strong> ist ein wiederkehrender Termin.</Typography>
                <Typography variant="body2" color="text.secondary">
                  Soll nur dieser einzelne Termin ({deleteConfirm.item?.date?.split('-').reverse().join('.')}) oder die gesamte Serie gelöscht werden?
                </Typography>
              </Box>
            ) : (
              <Typography>
                {deleteConfirm.type === 'event'
                  ? `„${deleteConfirm.item?.name}" wird endgültig gelöscht.`
                  : `„${deleteConfirm.item?.firstName} ${deleteConfirm.item?.lastName}" wird endgültig entfernt.`}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
            <Button onClick={() => setDeleteConfirm({ open: false, type: null, item: null })} color="inherit" sx={{ mr: 'auto' }}>Abbrechen</Button>
            {deleteConfirm.type === 'event' && deleteConfirm.item?.recurring ? (
              <>
                <Button variant="outlined" color="error" onClick={() => handleDeleteEvent(deleteConfirm.item.id, deleteConfirm.item.date)}>Nur dieser Termin</Button>
                <Button variant="contained" color="error" onClick={() => handleDeleteEvent(deleteConfirm.item.id)}>Gesamte Serie</Button>
              </>
            ) : (
              <Button variant="contained" color="error" onClick={() => {
                if (deleteConfirm.type === 'event') handleDeleteEvent(deleteConfirm.item.id);
                else handleDeletePlayer(deleteConfirm.item.id);
              }}>Löschen</Button>
            )}
          </DialogActions>
        </Dialog>

        <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack({ ...snack, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snack.severity} variant="filled" onClose={() => setSnack({ ...snack, open: false })}>{snack.msg}</Alert>
        </Snackbar>

        <ChangePasswordDialog open={changePasswordOpen} forced={!!currentUser?.mustChangePassword}
          onSuccess={(updatedUser) => { setCurrentUser(updatedUser); setChangePasswordOpen(false); showSnack('Passwort erfolgreich geändert!'); }}
          onCancel={() => setChangePasswordOpen(false)} />

        <SetPasswordDialog open={setPwdDialog.open} playerName={setPwdDialog.playerName}
          firstName={setPwdDialog.firstName}
          loginEmail={setPwdDialog.loginEmail} requireEmail={setPwdDialog.requireEmail}
          onConfirm={(password, email) => handleSetPassword(setPwdDialog.playerId, password, email)}
          onCancel={() => setSetPwdDialog({ open: false, playerId: null, playerName: '', firstName: '', loginEmail: '', requireEmail: false })} />
      </Box>
    </ThemeProvider>
  );
}
